import React, { FunctionComponent, useState, useEffect, useCallback, BaseSyntheticEvent } from 'react'
import { Form, FormikProps } from 'formik'
import { setCountryInCookie } from '@common/utils/cookie-utils'
import { CommonButton, ConvertStringToReact, LoadingSpinner } from '@common/components'
import { isEmpty } from 'lodash-es'
import { getSessionState, saveSessionState, connectRedux, GoToComponentProps, scrollToTop } from '@common/utils'
import { useMetrixScript } from '@common/hooks'
import { removeIsThirdPartyCookieDisabled } from '@buy/utils/get-is-thirdparty-cookie-disabled'
import {
    mapBuyCheckoutFormState,
    mapBuyCheckoutFormActions,
    BuyCheckoutFormActionsType,
} from '@buy/connectedComponents/CheckoutForm/connector'
import { isAddressComplete, isValidationAddressEligible } from '@buy/utils'
import {
    ConnectedBuyAccountFormInfo,
    BuyAccountFormInfoComponentProps,
    ConnectedBuyBillingFormInfo,
    BuyBillingFormInfoComponentProps,
    ConnectedBuyCheckoutFormSubmission,
    BuyCheckoutFormSubmissionComponentProps,
    CountryCodeInfo,
} from '@buy/connectedComponents'
import { BuyCheckoutFormState } from '@buy/connectedComponents/CheckoutForm/state'
import { ConnectedModal } from '@common/connectedComponents/Modal'
import {
    CHECKOUTFORM_FIELDS,
    CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES,
    BUY_PARTIAL_NON_VALIDATION_USER_TYPES,
    BUY_USER_TYPES,
    CHECKOUTFORM_VALIDATION_HARD_EXCLUDE_FIELD_TYPES,
} from '@buy/constants'
import { BUY_TRIAL_ERROR_IDS } from '@buy/constants/error-strings'
import { useCheckoutFormPageTracking } from '@buy/hooks'
import { BuyErrorContent } from '@buy/state/checkout-form-container/state'
import { BuyFormData } from '@buy/state/buy-form/state'
import { COUNTRIES, STORAGE_CONST } from '@common/constants'
import { FATAL_ERROR_TYPE } from '@common/state/fatal-error/state'
import { FLEX_KEY } from '@common/constants/flex'
import { METRIX_MERCHANTID_GETGO_US } from '@common/constants/metrix'
import { useSearchParams } from 'react-router-dom'
import { showDisclaimer } from '@common/constants/search-query-params'
import { addressValidationMessage } from '@common/constants/error-messages'
import checkoutStyles from './checkout-form.module.css'

type BuyCheckoutFormProps = GoToComponentProps<
    BuyCheckoutFormState,
    BuyCheckoutFormActionsType,
    {
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
        initialData: BuyFormData
        errorContent: BuyErrorContent
        formikRef: React.RefObject<FormikProps<BuyFormData>>
        setHasAddressValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        setGeneralFieldErrors: React.Dispatch<React.SetStateAction<boolean>>
        setHasCardValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
        hasAddressValidationErrors: boolean
        generalFieldErrors: boolean
        hasCardValidationErrors: boolean
        isSubmittingRef: React.RefObject<boolean>
        countryCodeInfo: CountryCodeInfo
        setCountryCodeInfo: React.Dispatch<React.SetStateAction<CountryCodeInfo>>
        formikProps: FormikProps<BuyFormData>
    }
>

const CheckoutForm: FunctionComponent<BuyCheckoutFormProps> = ({ state, actions, props }) => {
    const isInHiddenSilentAuthIFrame = !!window.sessionStorage.getItem('IsSilentAuth')
    const { inlineErrors, clearEmail, content, statesOptional, fingerprint, totalLoaded, isUserLoggedIn, location } =
        state
    const {
        formikRef,
        setHasAddressValidationErrors,
        setGeneralFieldErrors,
        setHasCardValidationErrors,
        generalFieldErrors,
        hasAddressValidationErrors,
        isSubmittingRef,
        countryCodeInfo,
        setCountryCodeInfo,
        formikProps,
    } = props
    const { setCheckoutFormInlineError, removeCheckoutFormInlineError, calculatePrice } = actions

    const [isSubmitting, setIsSubmitting] = useState(false)
    const isCrossProductFlow = state.isUserLoggedIn && state.userType === BUY_USER_TYPES.CROSSPRODUCT
    const [isAutoSubmitting] = useState(isInHiddenSilentAuthIFrame || isCrossProductFlow)
    const [useSameForBilling, setUseSameForBilling] = useState(sessionStorage.getItem('useSameForBilling') !== 'false')
    const [isFormReset, setIsFormReset] = useState(false)
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
    const [showCurrencyModal, setCurrencyModal] = useState(false)
    const [showCardinalModal, setCardinalModal] = useState(false)
    const [isTaxRequired, setTaxRequired] = useState(false)
    const [onInitialValue, setOnInitialValue] = useState<Record<string, string>>({})
    const [searchParams] = useSearchParams()
    const countryCode = formikRef.current?.values.CountryCode
    const [isDisabled, setIsDisabled] = useState(false)

    const isTrialUser = state.userType === BUY_USER_TYPES.IDENTITY_ONLY || state.userType === BUY_USER_TYPES.TRIALER
    const isExpiredTrialUser = state.userType === BUY_USER_TYPES.EXPIRED

    const resetFieldTouched = useCallback(() => {
        if (formikRef?.current) {
            Object.keys(formikRef.current.values).forEach((fieldKey) => {
                formikRef.current?.setFieldTouched(fieldKey, false)
            })
        }
    }, [formikRef])

    const canValidateAddress = (values: BuyFormData, dontForceCompare?: boolean) => {
        // for later: need to send a new FormData object with billing first name and billing last name instead of FN & LN
        if (
            isValidationAddressEligible(values, dontForceCompare) &&
            isAddressComplete(values, statesOptional, isTaxRequired)
        ) {
            actions.validateAddress(values, (value: boolean) => {
                setIsDisabled(value)
            })
            actions.calculatePrice(values)
        }
    }

    const resetFatalError = () => {
        if (isFormReset && state.isFatalError) {
            actions.setFatalError(false)
            setIsFormReset(false)
        }
    }

    // tracking
    const { trackFormError, trackBillingInfoStep, trackPaymentStep, trackSubmitForm } = useCheckoutFormPageTracking({
        planName: state.selectedPlan.name,
        formikRef,
        dependencies: [state.selectedPlan.name, state.baseLine.key],
    })

    // Establish device fingerprint session for decisionmaker
    // See documentation at https://confluence.ops.expertcity.com/x/L4UVIg
    const sessionId = fingerprint
    const orgId = FLEX_KEY
    const mid = METRIX_MERCHANTID_GETGO_US
    useMetrixScript(!!fingerprint, orgId, sessionId, mid)

    const submitForm = useCallback(
        (formValues: BuyFormData) => {
            // Since the OnePay flow doesn't show the credit card form on the ecomm site,
            // track the "begin_checkout step 4" event upon clicking the Proceed to Purchase button
            // See also: src/products/buy/connectedComponents/PaymentFormInfo/index.tsx
            // See also: src/products/buy/utils/buy-tracking.ts
            trackPaymentStep()

            try {
                const qualtricsData = getSessionState('qualtrics_data_gh')
                saveSessionState('qualtrics_data_gh', {
                    ...(!qualtricsData ? {} : qualtricsData),
                    userEmail: formValues.Email,
                })
            } catch (err) {
                console.error(err)
            }
            setIsSubmitting(true)

            if (isCrossProductFlow) {
                // console.log('submitting cross product')
                actions.existingUserSubmitFormUCS(formValues)
            } else {
                // console.log('submitting new user or trial conversion')
                actions.submitFormUcs(formValues)
            }

            trackSubmitForm()
        },
        [actions, trackPaymentStep, trackSubmitForm, isCrossProductFlow]
    )

    const onFormErrors = (formIds: string[], formErrors: string[]) => {
        scrollToTop()
        setGeneralFieldErrors(true)
        trackFormError(formIds, formErrors)
    }

    const submitExistingUser = (values: BuyFormData) => {
        setIsSubmitting(true)
        trackSubmitForm()
        actions.existingUserSubmitFormUCS(values)
    }

    const preSubmit = () => {
        if (hasAddressValidationErrors) {
            scrollToTop()
            if (hasAddressValidationErrors) {
                trackFormError([BUY_TRIAL_ERROR_IDS.ADDRESS_ERROR], ['invalid billing address'])
            }
        } else if (isCrossProductFlow) {
            if (formikRef?.current) {
                if (searchParams.get(showDisclaimer) === 'true')
                    if (countryCodeInfo.current !== COUNTRIES.US) submitExistingUser(formikRef.current.values)
                    else {
                        formikRef.current.validateForm().then((errors) => {
                            if (isEmpty(errors[CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED]) && formikRef?.current)
                                submitExistingUser(formikRef.current.values)
                            else
                                onFormErrors(
                                    [CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED],
                                    [errors[CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED]!]
                                )
                        })
                    }
                else submitExistingUser(formikRef.current.values)
            }
        } else if (formikRef?.current) {
            formikRef.current.validateForm().then((errors) => {
                if (statesOptional && errors.StateCode) {
                    /* eslint-disable no-param-reassign */
                    delete errors.StateCode
                }
                if (
                    (countryCodeInfo.current !== COUNTRIES.US && errors.Disclaimer) ||
                    searchParams.get(showDisclaimer) === 'false'
                ) {
                    /* eslint-disable no-param-reassign */
                    delete errors.Disclaimer
                }
                if (!isTaxRequired && errors.TaxNumber) {
                    /* eslint-disable no-param-reassign */
                    delete errors.TaxNumber
                }
                if (window.sessionStorage.getItem(STORAGE_CONST.USER_TYPE) === BUY_USER_TYPES.NEW) {
                    // if userType was saved to sessionStorage for a new user before the redirect to OnePay but the user
                    // clicked Back on onepay, they'll end up here when they try to click Proceed to Purchase again.
                    // see src/products/buy/saga/checkout-form/form-submission-ucs.ts
                    delete errors.Password
                }
                const errorList = Object.keys(errors)

                if (errors.Password && formikRef?.current) {
                    formikRef.current.touched.Password = true
                }
                if (errorList.length > 0) {
                    if (isTrialUser || isExpiredTrialUser) {
                        const trialUserValidErrors = Object.entries(errors).filter(
                            (errorField) => errorField[0] !== CHECKOUTFORM_FIELDS.PASSWORD
                        )
                        if (Object.keys(trialUserValidErrors).length > 0) {
                            const trialUserValidErrorValues = trialUserValidErrors.map(
                                (fieldErrorTuple) => fieldErrorTuple[1]
                            )
                            const trialUserValidErrorKeys = trialUserValidErrors.map(
                                (fieldErrorTuple) => fieldErrorTuple[0]
                            )
                            onFormErrors(trialUserValidErrorKeys, trialUserValidErrorValues as string[])
                        } else if (formikRef.current) submitForm(formikRef.current.values)
                    } else {
                        onFormErrors(Object.keys(errors), Object.values(errors) as string[])
                    }
                } else if (formikRef.current) {
                    submitForm(formikRef.current.values)
                }
            })
        }
    }

    const checkCurrencyConflict = (country: string, originCountry: string) => {
        if (originCountry !== country) {
            setCurrencyModal(true)
        } else {
            setCurrencyModal(false)
        }
    }
    //
    useEffect(() => {
        if (formikProps.values) {
            if (statesOptional) {
                removeCheckoutFormInlineError('StateCode')
            }
            canValidateAddress(formikProps.values, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formikProps.values.FirstName,
        formikProps.values.LastName,
        formikProps.values.Email,
        formikProps.values.Street1,
        formikProps.values.City,
        formikProps.values.ZipCode,
        formikProps.values.StateCode,
        formikProps.values.ContactPhone,
        statesOptional,
    ])

    useEffect(() => {
        if (!Object.keys(inlineErrors).length) {
            setGeneralFieldErrors(false)
        }
    }, [inlineErrors, setGeneralFieldErrors])

    useEffect(() => {
        if (state.userType === BUY_USER_TYPES.ADDON && formikRef.current) {
            calculatePrice(formikRef.current.values)
        }
    }, [state.userType, calculatePrice, formikRef])

    useEffect(() => {
        if (isInHiddenSilentAuthIFrame && formikRef.current && countryCode) {
            // console.log('1. Calculate Price')
            calculatePrice(formikRef.current!.values)
        }
    }, [isInHiddenSilentAuthIFrame, formikRef, countryCode, calculatePrice])

    useEffect(() => {
        if ((isCrossProductFlow || isInHiddenSilentAuthIFrame) && totalLoaded && !isSubmitting) {
            // Now that we are auto-submitting the form after returning from auto-login, remove the flag from session storage
            window.sessionStorage.removeItem('IsSilentAuth')

            // console.log('3. Submit Form')
            removeIsThirdPartyCookieDisabled() // Remove the value from sessionStorage now that we are done with it
            submitForm(formikRef.current!.values)
        }
    }, [isInHiddenSilentAuthIFrame, totalLoaded, formikRef, submitForm, isSubmitting, isCrossProductFlow])

    // show form errors by scrolling to top
    useEffect(() => {
        const errorsInForm = generalFieldErrors || hasAddressValidationErrors
        if (errorsInForm) {
            scrollToTop()
            setIsSubmitting(false)
            if ((window as any).QSI) {
                ;(window as any).QSI.API?.unload()
            }
        }
    }, [hasAddressValidationErrors, generalFieldErrors])

    // reset the form and reset the states affected by checkout form
    useEffect(() => {
        if (state.isFatalError && formikRef.current) {
            const hasAddressErrors = location.addressErrors.find((item) => item.message === addressValidationMessage)
            if (isEmpty(hasAddressErrors)) scrollToTop()
            formikRef.current.setSubmitting(false)
            setIsSubmitting(false)
            resetFieldTouched()
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.EXPIRATION_DATE, '')
            setHasCardValidationErrors(false)
            setGeneralFieldErrors(false)
            setIsFormReset(true)
            if (
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_SETUP ||
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_CHALLENGE ||
                state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_PAYMENT
            ) {
                setCardinalModal(true)
            }
            if ((window as any).QSI) {
                ;(window as any).QSI.API?.unload()
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions, formikRef, isFormReset, resetFieldTouched, state.isFatalError])

    // validate the non empty entries after populating
    useEffect(() => {
        if (state.userType !== BUY_USER_TYPES.ADDON) {
            Object.entries(props.initialData).forEach(([formField, formValue]) => {
                // ignore empty fields
                if (formValue !== '') {
                    // if the user is a trialer, crossproduct, expired, banned
                    // then dont validate the identity and address fields
                    if (
                        !(
                            Object.values(BUY_PARTIAL_NON_VALIDATION_USER_TYPES).includes(
                                state.userType as BUY_PARTIAL_NON_VALIDATION_USER_TYPES
                            ) &&
                            Object.values(CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES).includes(
                                formField as CHECKOUTFORM_VALIDATION_EXCLUDE_FIELD_TYPES
                            )
                        ) &&
                        !Object.values(CHECKOUTFORM_VALIDATION_HARD_EXCLUDE_FIELD_TYPES).includes(
                            formField as CHECKOUTFORM_VALIDATION_HARD_EXCLUDE_FIELD_TYPES
                        )
                    ) {
                        if (formikRef.current) {
                            formikRef.current.validateField(formField)
                        }
                    }
                }
            })
        }
    }, [formikRef, props.initialData, state.userType])

    // if the billing frequency changes, recalculate the cost
    useEffect(() => {
        if (formikRef.current && isAddressComplete(formikRef.current.values, statesOptional, isTaxRequired)) {
            calculatePrice(formikRef.current.values)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculatePrice, formikRef, state.billingFrequency])

    // for trial and expired trial user, set billing FN and LN to avoid validation error
    useEffect(() => {
        if ((isTrialUser || isExpiredTrialUser) && useSameForBilling && formikRef.current) {
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME, props.initialData.FirstName)
            formikRef.current.setFieldValue(CHECKOUTFORM_FIELDS.BILLING_LAST_NAME, props.initialData.LastName)
        }
    }, [isTrialUser, useSameForBilling, props.initialData, isExpiredTrialUser, formikRef])

    const accountInfoProps: BuyAccountFormInfoComponentProps = {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
        isSubmittingRef,
        setOnInitialValue,
        isUserLoggedIn,
        countryCodeInfo,
    }

    const billingInfoProps: BuyBillingFormInfoComponentProps = {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        setUseSameForBilling,
        trackBillingInfoStep,
        setHasAddressValidationErrors,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
        checkCurrencyConflict,
        setTaxRequired,
        countryCodeInfo,
        setCountryCodeInfo,
        setIsDisabled,
    }

    const checkoutSubmissionProps: BuyCheckoutFormSubmissionComponentProps = {
        isSubmitting,
        preSubmit,
        isTabletOrMobileView: props.isTabletOrMobileView,
        inlineErrors,
        isOnePayFlow: true,
        isDisabled,
    }

    const trackAbandon = (value: BaseSyntheticEvent) => {
        if (formikRef.current) {
            const formData = {
                firstName: formikRef.current.values.FirstName,
                lastName: formikRef.current.values.LastName,
                email: formikRef.current.values.Email,
            }

            const fieldName = value.target.name
            const currentValue = value.target.value
            const fieldsToValidate = [
                CHECKOUTFORM_FIELDS.EMAIL,
                CHECKOUTFORM_FIELDS.FIRST_NAME,
                CHECKOUTFORM_FIELDS.LAST_NAME,
            ]
            if (fieldsToValidate.includes(fieldName)) {
                const initialValue = onInitialValue[fieldName]
                formikRef.current.validateField(fieldName).then(() => {
                    const hasFieldErrors = Boolean(
                        formikRef.current?.errors.Email ||
                            formikRef.current?.errors.FirstName ||
                            formikRef.current?.errors.LastName
                    )
                    if (!isEmpty(formData.email) && currentValue !== initialValue && !hasFieldErrors) {
                        actions.submitAbandonCart(formData.email, formData)
                    }
                })
            }
        }
    }

    return (
        <>
            <Form onBlur={(value) => trackAbandon(value)}>
                <ConnectedBuyAccountFormInfo {...accountInfoProps} />
                <ConnectedBuyBillingFormInfo {...billingInfoProps} />
                <ConnectedBuyCheckoutFormSubmission {...checkoutSubmissionProps} />
            </Form>
            <ConnectedModal isOpen={showCardinalModal} closeable={false}>
                <div>
                    <h3>Oh no!</h3>
                    <div className={checkoutStyles['currency-modal-desc']}>
                        {state.fatalErrorType === FATAL_ERROR_TYPE.CARDINAL_CHALLENGE
                            ? content.errorCardinalChallenge
                            : content.errorCardinalSetup}
                    </div>
                    <div>
                        <CommonButton
                            onClick={() => {
                                window.location.reload()
                            }}
                        >
                            {content.modalCloseButtonText}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>
            <ConnectedModal isOpen={showCurrencyModal} closeable={false}>
                <div>
                    <h3>{content.changeCountryHeader}</h3>
                    <div className={checkoutStyles['currency-modal-desc']}>
                        <ConvertStringToReact htmlString={content.changeCountryContent} />
                    </div>
                    <div>
                        <CommonButton
                            onClick={() => {
                                const currentForm = formikRef.current
                                if (currentForm) {
                                    sessionStorage.setItem(CHECKOUTFORM_FIELDS.COUNTRY_CODE, countryCodeInfo.current)
                                    setCountryInCookie(countryCodeInfo.current)
                                    sessionStorage.removeItem(CHECKOUTFORM_FIELDS.STREET1)
                                    sessionStorage.removeItem(CHECKOUTFORM_FIELDS.CITY)
                                    sessionStorage.removeItem(CHECKOUTFORM_FIELDS.STATE_CODE)
                                    sessionStorage.removeItem(CHECKOUTFORM_FIELDS.ZIP_CODE)
                                    sessionStorage.removeItem(CHECKOUTFORM_FIELDS.CONTACT_PHONE)
                                    window.location.reload()
                                }
                            }}
                            modifier={checkoutStyles['currency-modal-cta']}
                        >
                            {content.modalReload}
                        </CommonButton>
                        <CommonButton
                            purpose="quaternary"
                            onClick={() => {
                                if (formikRef.current) {
                                    // currentForm.values.CountryCode = currentForm.initialValues.CountryCode // Always use setFieldValue to set form values
                                    sessionStorage.setItem(CHECKOUTFORM_FIELDS.COUNTRY_CODE, countryCodeInfo.previous!)
                                    formikRef.current?.setFieldValue(
                                        CHECKOUTFORM_FIELDS.COUNTRY_CODE,
                                        countryCodeInfo.previous!
                                    )
                                    setCountryCodeInfo({ current: countryCodeInfo.previous!, previous: '' })
                                }
                                setCurrencyModal(false)
                            }}
                        >
                            {content.modalCancel}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>
            <ConnectedModal isOpen={clearEmail} closeable={false}>
                <div>
                    <h3>{content.existingUsersModalHeader}</h3>
                    <div className={checkoutStyles['existing-email-text']}>
                        <ConvertStringToReact htmlString={content.existingUsersModalContent} />
                    </div>
                    <div className={checkoutStyles['existing-email-ctas']}>
                        <CommonButton
                            onClick={() => actions.userLogin(window.location.pathname)}
                            qaTag="login-error-button"
                        >
                            {content.signIn}
                        </CommonButton>
                        <CommonButton purpose="tertiary" onClick={() => actions.setEmailClear(false)}>
                            {content.existingUsersNewEmailButtonText}
                        </CommonButton>
                    </div>
                </div>
            </ConnectedModal>
            <ConnectedModal isOpen={isPaymentModalOpen} closeable onClose={() => setIsPaymentModalOpen(false)}>
                <div>
                    <h3>{content.changePaymentMethodModalHeader}</h3>
                    <div>
                        <ConvertStringToReact htmlString={content.changePaymentMethodModalSubheader} />
                    </div>
                    <CommonButton onClick={() => setIsPaymentModalOpen(false)}>
                        {content.modalCloseButtonText}
                    </CommonButton>
                </div>
            </ConnectedModal>

            {(isSubmitting || isAutoSubmitting) && (
                <LoadingSpinner text={state.loadingSpinnerText} wrapperModifier={checkoutStyles['submit-blocker']} />
            )}
        </>
    )
}

export const ConnectedCheckoutFormOnePay = connectRedux(
    CheckoutForm,
    mapBuyCheckoutFormState,
    mapBuyCheckoutFormActions
)
